import SplitVerticalPanels from "components/SplitVerticalPanels";
import { idPropType } from "constants/app";
import { find } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setIsSidebarCollapsed } from "slices/app/appSlice";
import { selectIsSidebarCollapsed } from "slices/app/selectors";
import { fetchDocumentParagraphs } from "slices/proposalOutline/actions/outlineActions";
import {
  buildCoverageRows,
  buildDocumentOptions,
  buildParagraphRows,
  buildRowIndex,
  buildSectionRows,
  buildSectionTraceSteps,
  countRowSummary,
  filterVisibleSectionRows,
  findRowForStep,
  paragraphDistanceFor,
  refLabelsOf,
} from "slices/proposalOutline/coverage";
import {
  selectCoverage,
  selectDocumentParagraphs,
  selectHasLoadedDocumentParagraphs,
  selectOutlineDocuments,
  selectOutlineOwner,
  selectPrimaryOutlineDocumentId,
  selectRequirementByRefLabel,
  selectRequirements,
} from "slices/proposalOutline/selectors";
import isNullish from "utils/isNullish";

import CoverageDocumentTabs from "./components/CoverageDocumentTabs";
import CoverageRequirementsTable from "./components/CoverageRequirementsTable";
import ExtractionReuseWarning from "./components/ExtractionReuseWarning";
import {
  COVERAGE_DOCUMENT_MAX_WIDTH,
  COVERAGE_DOCUMENT_MIN_WIDTH,
  COVERAGE_DOCUMENT_WIDTH,
  COVERAGE_PANE_NARROW_WIDTH,
  COVERAGE_SECTIONS_MAX_WIDTH,
  COVERAGE_SECTIONS_MIN_WIDTH,
  COVERAGE_SECTIONS_WIDTH,
  COVERAGE_TABLE_MIN_WIDTH,
} from "./constants";
import CoverageDocumentPane from "./CoverageDocumentPane";
import CoverageSectionsPane from "./CoverageSectionsPane";
import styles from "./CoverageTab.module.scss";
import CoverageTabContext from "./CoverageTabContext";
import { buildDocumentLabelsById } from "./gates/requirementsReviewColumns";

// A stable identity, so the table's scroll effect does not re-run on every render while loading.
const NO_ROWS = [];

const CoverageTab = ({ traceSectionId = null }) => {
  const dispatch = useDispatch();
  const coverage = useSelector(selectCoverage);
  const requirements = useSelector(selectRequirements);
  const requirementByRefLabel = useSelector(selectRequirementByRefLabel);
  const outlineDocuments = useSelector(selectOutlineDocuments);
  const owner = useSelector(selectOutlineOwner);
  const primaryDocumentId = useSelector(selectPrimaryOutlineDocumentId);

  const isSidebarCollapsed = useSelector(selectIsSidebarCollapsed);

  const [activeSectionId, setActiveSectionId] = useState(traceSectionId);
  const tableRef = useRef();
  const viewerRef = useRef();
  const didCollapseSidebar = useRef(false);

  const [collapsedSectionIds, setCollapsedSectionIds] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);
  const [isSectionsNarrow, setIsSectionsNarrow] = useState(false);
  const [isDocumentNarrow, setIsDocumentNarrow] = useState(false);
  const [sectionsWidth, setSectionsWidth] = useState(COVERAGE_SECTIONS_WIDTH);
  const [documentWidth, setDocumentWidth] = useState(COVERAGE_DOCUMENT_WIDTH);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [traceStepIndex, setTraceStepIndex] = useState(0);
  const [isScrollPending, setIsScrollPending] = useState(
    traceSectionId != null
  );

  const documentOptions = useMemo(
    () => buildDocumentOptions(outlineDocuments),
    [outlineDocuments]
  );
  // A re-fetched outline can drop the document that was picked, so the selection is only honoured
  // while it is still one of the outline's source documents.
  const documentId = documentOptions.some(
    (option) => option.id === selectedDocumentId
  )
    ? selectedDocumentId
    : primaryDocumentId;
  const paragraphs = useSelector(selectDocumentParagraphs(documentId));
  const hasLoadedParagraphs = useSelector(
    selectHasLoadedDocumentParagraphs(documentId)
  );
  const activeDocumentIndex = documentOptions.findIndex(
    (option) => option.id === documentId
  );

  useEffect(() => {
    if (owner && documentId != null) {
      dispatch(
        fetchDocumentParagraphs(owner.ownerType, owner.ownerId, documentId)
      );
    }
  }, [dispatch, owner, documentId]);

  useEffect(() => {
    if (traceSectionId != null) {
      setActiveSectionId(traceSectionId);
      setTraceStepIndex(0);
      setIsScrollPending(true);
    }
  }, [traceSectionId]);

  // The nav belongs to the app, not to this tab: leaving with the table expanded must not strand
  // it collapsed.
  useEffect(
    () => () => {
      if (didCollapseSidebar.current) {
        dispatch(setIsSidebarCollapsed(false));
      }
    },
    [dispatch]
  );

  const sectionRows = useMemo(() => buildSectionRows(coverage), [coverage]);
  const visibleSectionRows = useMemo(
    () => filterVisibleSectionRows(sectionRows, collapsedSectionIds),
    [sectionRows, collapsedSectionIds]
  );

  const activeSection = useMemo(() => {
    if (isNullish(activeSectionId)) return null;

    return coverage.sections.find((s) => s.id === activeSectionId) || null;
  }, [coverage.sections, activeSectionId]);

  // A fetch is only outstanding when there is both an owner to fetch for and a document to fetch —
  // without either there is nothing to wait for, so the requirement list is the honest thing to show
  // rather than a spinner that would never resolve.
  const isLoadingParagraphs =
    Boolean(owner) && documentId != null && !hasLoadedParagraphs;

  // Always the document itself, never a section's subset: a reader needs the paragraphs around a
  // requirement, including the ones no requirement came out of. Selecting a section navigates within
  // this list rather than shortening it.
  //
  // Nothing is listed until the fetch settles. Showing the requirement fallback in the meantime put
  // a different list, in a different order, under a different summary on screen for one round trip.
  const allRows = useMemo(() => {
    if (isLoadingParagraphs) {
      return NO_ROWS;
    }
    if (paragraphs.length > 0) {
      return buildParagraphRows({
        paragraphs,
        requirements,
        coverage,
        documentId,
      });
    }
    return buildCoverageRows({
      coverage,
      requirements,
      activeSection: null,
      requirementByRefLabel,
    });
  }, [
    coverage,
    requirements,
    requirementByRefLabel,
    paragraphs,
    documentId,
    isLoadingParagraphs,
  ]);

  // Lookups rather than scans. activeRow, the trace step's row and the section's first row are all
  // read on every render — including hovers and pane toggles, which cannot change the rows — and
  // each used to walk the whole list. On a 4000-paragraph document that was four full passes per
  // render.
  const { rowsById, rowIndexById, rowIndexByPointId } = useMemo(
    () => buildRowIndex(allRows),
    [allRows]
  );

  const isParagraphView = paragraphs.length > 0;
  const rows = allRows;
  const lastParagraphIndex = paragraphs.length ? paragraphs.length - 1 : 0;

  const documentLabelsById = useMemo(
    () => buildDocumentLabelsById(outlineDocuments),
    [outlineDocuments]
  );

  const activeRow = rowsById.get(activeRowId) || null;
  const activeRequirement = activeRow?.requirement || null;
  const activeRowIndex = rowIndexById.get(activeRowId) ?? -1;

  const traceSteps = useMemo(
    () =>
      buildSectionTraceSteps({
        activeSection,
        coverage,
        requirements,
        requirementByRefLabel,
      }),
    [activeSection, coverage, requirements, requirementByRefLabel]
  );
  const currentStep = traceSteps[traceStepIndex] || null;

  const highlightedRowIndex = findRowForStep({
    rows,
    step: currentStep,
    rowIndexByPointId,
  });
  const steppedRowId =
    highlightedRowIndex >= 0 ? rows[highlightedRowIndex].id : null;

  // A section can have requirements and still have no trace step: a step needs paragraph provenance
  // (paragraphIndexStart) to have somewhere to scroll to, and a requirement can legitimately lack it
  // — an unverified one, or a container section with nothing linked at all. Without this fallback the
  // settle effect below early-returns forever, so selecting such a section highlighted nothing and
  // left isScrollPending armed. Falling back to the section's first row means selecting a section
  // always lands somewhere it owns.
  const sectionRefLabels = useMemo(
    () => new Set(refLabelsOf(coverage, activeSection)),
    [coverage, activeSection]
  );
  const firstSectionRowId = useMemo(() => {
    let earliest = -1;
    sectionRefLabels.forEach((refLabel) => {
      const index = rowIndexByPointId.get(refLabel);
      if (index != null && (earliest < 0 || index < earliest)) {
        earliest = index;
      }
    });
    return earliest < 0 ? null : rows[earliest].id;
  }, [rows, rowIndexByPointId, sectionRefLabels]);

  // Only when there are NO steps at all. A section that has steps owns its highlight through the
  // stepper, including the deliberate case where the current step's row is absent because it lives in
  // a document whose paragraphs are still in flight — the effect below waits for it. Falling back
  // there would abandon that wait and jump to an unrelated row.
  const highlightedRowId =
    traceSteps.length > 0 ? steppedRowId : firstSectionRowId;

  // The id rather than the step object, so a store refresh giving coverage a new identity is not
  // mistaken for the reader going somewhere.
  const currentStepDocumentId = currentStep?.documentId ?? null;

  // The document follows the step only when a navigation asked for it, which is exactly what a
  // pending scroll means — so a document the reader picked from the tabs is never overridden, and
  // stepping still aligns even when it returns to the document the previous step was in.
  useEffect(() => {
    if (isScrollPending && currentStepDocumentId != null) {
      setSelectedDocumentId(currentStepDocumentId);
    }
  }, [isScrollPending, currentStepDocumentId]);

  // Never settle against a list that is about to be replaced. Once the fetch has settled the list on
  // screen is the final one, whether that is the document's paragraphs or the requirement fallback
  // for a document that genuinely has none.
  const canSettleScroll = !isLoadingParagraphs;

  // The row to scroll to may not exist yet — a step can switch documents, and that document's
  // paragraphs are still in flight. The debt is settled once a matching row appears.
  useEffect(() => {
    if (!isScrollPending || highlightedRowId == null || !canSettleScroll) {
      return;
    }
    setActiveRowId(highlightedRowId);
    setIsScrollPending(false);
  }, [isScrollPending, highlightedRowId, canSettleScroll]);

  // What the document pane scrolls to. A requirement row aims at its verbatim quote; a plain
  // paragraph row aims at its own text, positioned by its index over the document's paragraph count.
  const scrollTarget = useMemo(() => {
    if (!activeRow) {
      return null;
    }
    if (activeRow.paragraphIndex != null && lastParagraphIndex > 0) {
      return {
        text: activeRow.text,
        pageNumber: activeRow.page ?? undefined,
        paragraphDistance: Math.min(
          1,
          activeRow.paragraphIndex / lastParagraphIndex
        ),
      };
    }
    if (!activeRequirement) {
      return null;
    }
    return {
      text: activeRequirement.verbatimQuote,
      pageNumber: activeRequirement.pageNumber,
      paragraphDistance: paragraphDistanceFor(activeRequirement, requirements),
    };
  }, [activeRow, activeRequirement, lastParagraphIndex, requirements]);

  // Which of the addressing section's steps the clicked row is, so the sections pane's stepper
  // opens on the reader's row rather than restarting the section at 1.
  const stepIndexOfRowInSection = useCallback(
    (row, section) => {
      const steps = buildSectionTraceSteps({
        activeSection: section,
        coverage,
        requirements,
        requirementByRefLabel,
      });
      const index = steps.findIndex((step) =>
        (row.pointIds || []).includes(step.pointId)
      );

      return Math.max(index, 0);
    },
    [coverage, requirements, requirementByRefLabel]
  );

  // A pill or the counter inside a row's Addressed In cell: the reader is choosing WHICH of the
  // sections addressing that row the left pane should show. The row they chose it from becomes the
  // selection too, so the gesture reads the same as clicking the row itself.
  const onAddressedInStepUpdate = useCallback(
    ({ row, title }) => {
      const match = find(coverage.sections, ["title", title]);

      if (!match) {
        return;
      }
      if (row) {
        setIsScrollPending(false);
        setActiveRowId(row.id);
        setTraceStepIndex(stepIndexOfRowInSection(row, match));
      }
      setActiveSectionId(match.id);
    },
    [coverage, stepIndexOfRowInSection]
  );

  const onSelectSection = (sectionId) => {
    const isClearing = activeSectionId === sectionId;

    setActiveSectionId(isClearing ? null : sectionId);
    setTraceStepIndex(0);
    setActiveRowId(null);
    setIsScrollPending(!isClearing);
  };

  const onSectionsStepUpdate = (e, newStep) => {
    e.stopPropagation();
    setTraceStepIndex(() => newStep - 1);

    setIsScrollPending(true);
  };

  // The clicked row becomes the highlight, which is what puts its text under the document
  // preview's own scroll-and-highlight — see scrollTarget above. The three cases the design
  // asks for differ only in what happens to the LEFT pane: a row no section addresses leaves
  // the pane alone, one that is addressed selects the first addressing section, and one
  // addressed several times additionally gets a counter in its own cell to walk them.
  const onSelectRow = (row) => {
    const { addressedIn = [] } = row;
    const [firstTitle] = addressedIn;

    setIsScrollPending(false);

    if (row.id === activeRowId) {
      setActiveRowId(null);
      setActiveSectionId(null);
      return;
    }

    setActiveRowId(row.id);

    const match = find(coverage.sections, ["title", firstTitle]);

    if (match) {
      setTraceStepIndex(stepIndexOfRowInSection(row, match));
      setActiveSectionId(match.id);
    }
  };

  const onToggleSection = (sectionId) =>
    setCollapsedSectionIds((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId]
    );

  const providerValue = useMemo(
    () => ({
      activeRowIndex,
      activeSection,
      onAddressedInStepUpdate,
    }),
    [activeRowIndex, activeSection, onAddressedInStepUpdate]
  );

  // The gap count is the one thing the table itself only shows a dash for, so it is stated here
  // rather than left to be counted row by row. In the paragraph view the counts describe the whole
  // document, not just the batch on screen, and the batch is stated separately.
  const { withRequirementsCount, gapCount } = useMemo(
    () => countRowSummary(allRows),
    [allRows]
  );
  // Only the multi-document case needs "in this document" — with a single source it is noise.
  const paragraphScope =
    documentOptions.length > 1 ? "paragraphs in this document" : "paragraphs";
  // Counting an unloaded list would print zeros and then correct itself — the same flash the loading
  // state exists to remove.
  const summary = isLoadingParagraphs
    ? "Loading Paragraphs…"
    : isParagraphView
      ? `${allRows.length} ${paragraphScope} · ${withRequirementsCount} with requirements`
      : `Showing ${allRows.length} ${
          allRows.length === 1 ? "requirement" : "requirements"
        } · ${allRows.length - gapCount} covered`;

  // The design's one-click "give the table the room" control: both side panes narrow together,
  // the app's own nav collapses with them, and the same button puts everything back.
  const isTableExpanded = isSectionsNarrow && isDocumentNarrow;
  const onToggleTableExpanded = () => {
    const nextExpanded = !isTableExpanded;

    setIsSectionsNarrow(nextExpanded);
    setIsDocumentNarrow(nextExpanded);

    // Only ever undo our own collapse — a reader who keeps the nav collapsed anyway should not
    // find this button expanding it for them.
    if (nextExpanded) {
      didCollapseSidebar.current = !isSidebarCollapsed;
      dispatch(setIsSidebarCollapsed(true));
    } else if (didCollapseSidebar.current) {
      didCollapseSidebar.current = false;
      dispatch(setIsSidebarCollapsed(false));
    }
  };

  const sectionsPanel = {
    size: isSectionsNarrow ? COVERAGE_PANE_NARROW_WIDTH : sectionsWidth,
    minSize: isSectionsNarrow
      ? COVERAGE_PANE_NARROW_WIDTH
      : COVERAGE_SECTIONS_MIN_WIDTH,
    maxSize: isSectionsNarrow
      ? COVERAGE_PANE_NARROW_WIDTH
      : COVERAGE_SECTIONS_MAX_WIDTH,
  };

  const documentPanel = {
    size: isDocumentNarrow ? COVERAGE_PANE_NARROW_WIDTH : documentWidth,
    minSize: isDocumentNarrow
      ? COVERAGE_PANE_NARROW_WIDTH
      : COVERAGE_DOCUMENT_MIN_WIDTH,
    maxSize: isDocumentNarrow
      ? COVERAGE_PANE_NARROW_WIDTH
      : COVERAGE_DOCUMENT_MAX_WIDTH,
  };

  // Remembering the dragged width only while the pane is wide is what lets expanding restore
  // the size the user chose, instead of snapping back to the design default.
  const onResizeEnd = ([nextSectionsWidth, , nextDocumentWidth]) => {
    if (!isSectionsNarrow) {
      setSectionsWidth(nextSectionsWidth);
    }
    if (!isDocumentNarrow) {
      setDocumentWidth(nextDocumentWidth);
    }
  };

  const centerColumn = (
    <div className={styles.center}>
      <CoverageDocumentTabs
        active={Math.max(activeDocumentIndex, 0)}
        isTableExpanded={isTableExpanded}
        options={documentOptions}
        onSelect={(opt) => {
          setSelectedDocumentId(opt.id);
          setActiveRowId(null);
        }}
        onToggleTableExpanded={onToggleTableExpanded}
      />

      <div className={styles.summaryRow}>
        <span className={styles.summary} title={summary}>
          {summary}
        </span>
      </div>

      <ExtractionReuseWarning />

      <CoverageRequirementsTable
        emptyMessage={
          isParagraphView
            ? "No paragraphs to show for this document."
            : "No requirements to show."
        }
        isLoading={isLoadingParagraphs}
        onSelectRow={onSelectRow}
        ref={tableRef}
        selectedRowIndex={activeRowIndex}
        rows={rows}
        scrollToRowId={isScrollPending ? highlightedRowId : null}
      />
    </div>
  );

  return (
    <div className={styles.coverage} data-testid="coverage-tab">
      <CoverageTabContext.Provider value={providerValue}>
        <SplitVerticalPanels
          onResizeEnd={onResizeEnd}
          panels={[
            {
              ...sectionsPanel,
              children: (
                <CoverageSectionsPane
                  activeSectionId={activeSectionId}
                  collapsedIds={collapsedSectionIds}
                  isNarrow={isSectionsNarrow}
                  onSelectSection={onSelectSection}
                  onStepUpdate={onSectionsStepUpdate}
                  onToggleNarrow={() =>
                    setIsSectionsNarrow((current) => !current)
                  }
                  onToggleSection={onToggleSection}
                  rows={visibleSectionRows}
                  traceStepCount={traceSteps.length}
                  traceStepIndex={traceStepIndex}
                />
              ),
            },
            {
              initialSize: "1",
              size: "1",
              minSize: COVERAGE_TABLE_MIN_WIDTH,
              children: centerColumn,
            },
            {
              ...documentPanel,
              children: (
                <CoverageDocumentPane
                  documentId={activeRequirement?.sourceDocumentId ?? documentId}
                  documentLabel={
                    documentLabelsById[
                      activeRequirement?.sourceDocumentId ?? documentId
                    ] || ""
                  }
                  isNarrow={isDocumentNarrow}
                  onToggleNarrow={setIsDocumentNarrow}
                  ref={viewerRef}
                  target={scrollTarget}
                />
              ),
            },
          ]}
        />
      </CoverageTabContext.Provider>
    </div>
  );
};

CoverageTab.propTypes = {
  traceSectionId: idPropType,
};

export default CoverageTab;
